// ONE LIVE READ: a REST query the account stream keeps fresh by RE-READING it.
//
// Before this, "live" was a recipe each screen re-typed: `useStreamTopics` for
// the topics, an `isLive` off its status, a `live` flag into the hook to turn
// its `refetchInterval` off, and a `liveRows` memo painting stream rows over the
// REST answer. Five copies (Home, Pending, Ci, FleetWork, Fleet Live), and every
// view outside them — a task detail, a run header — was not live at all.
//
// `createLiveQuery` is the whole recipe as one call, and it paints nothing from
// the stream. It:
//   1. reads REST through TanStack Query exactly as `createQuery` does;
//   2. registers the topics its entities live on, over the shell's ONE
//      connection (`useSharedHub` — never a socket of its own);
//   3. on a change to an entity it holds (by id, or any of a type) INVALIDATES
//      its query: a re-READ, never a patch from the echo (§ Boundary). A
//      connection's `ready` re-reads it too when its data predates the gap the
//      connection closes (`live-changes.ts`);
//   4. suspends its poll and its refetch-on-focus while the stream VOUCHES for
//      it, and resumes both the moment it does not — except `refreshMs`, a slow
//      re-read kept on for fields the stream deliberately does not signal.
//
// The change half (registration, matching, coalescing, and what "vouches"
// means) is `live-changes.ts` (`useLiveChanges`), shared with the reads that are
// not one query (a paged list re-reads by `reload()`). This file adds the one
// thing a single query does on a change: invalidate itself.

import {
  type CreateQueryResult,
  createQuery,
  type QueryClient,
  type QueryKey,
  type QueryOptions,
  useQueryClient,
} from '@tanstack/solid-query';
import { untrack } from 'solid-js';
import type { AccountStreamStatus } from '../accountStream';
import type { LiveInterest } from './entities';
import { useLiveChanges } from './live-changes';

/**
 * The poll a live read falls back to while the stream does not vouch for it —
 * the 30 s every overlay consumer already resumes (`STREAM_STALE_MS`'s window).
 */
export const LIVE_FALLBACK_POLL_MS = 30_000;

/** `createQuery`'s options, minus the two this primitive owns, plus what the read holds. */
export type LiveQueryOptions<T, K extends QueryKey = QueryKey> = Omit<
  QueryOptions<T, Error, T, K>,
  'refetchInterval' | 'refetchOnWindowFocus' | 'initialData'
> & {
  /** The entities this read's answer contains. Empty: nothing the stream knows. */
  readonly live: readonly LiveInterest[];
  /**
   * A slow re-read that KEEPS running while the stream vouches — for a read
   * carrying fields the stream deliberately does not signal: a machine's stats
   * and heartbeat (the `machines` topic's version ignores them, or every beat
   * would re-read every tab), a run pulse derived from run events that never
   * bump `tasks.updated_at`, and the writers `task_changes` cannot see
   * (`entities.ts`). Omitted: the stream is the whole truth while live.
   */
  readonly refreshMs?: number;
};

export type LiveQuery<T> = {
  readonly query: CreateQueryResult<T, Error>;
  /** This read's topics' connection status — the pill. `idle` with no shared stream. */
  readonly status: () => AccountStreamStatus;
  /** True while the stream vouches for every entity held (poll and focus refetch off). */
  readonly isLive: () => boolean;
};

/** Per cached `Query`: when its in-flight fetch started, and when the fetch behind its data did. */
type FetchStamps = { inflightAt?: number; dataAt?: number };

/**
 * When the fetch that produced each cached query's DATA started: a `ready`
 * re-reads only data read before its gap began (`live-changes.ts`). Read off the
 * cache's own events, not a wrapped queryFn, so it holds whoever fetched — a
 * second component on the key, a plain `createQuery`, a `fetchQuery` — and a
 * fetch that failed leaves the stamp of the data still shown. A per-instance
 * stamp used to read 0 for a cache-served component, and a landing time for data
 * no live read fetched: both re-read fresh data or skipped stale data. A
 * `WeakMap`, so a stamp is collected with the query the cache evicts.
 */
const STAMPS = new WeakMap<object, FetchStamps>();
const WATCHED = new WeakSet<QueryClient>();

/** Subscribe once per client. Lives as long as the client (the app's one, `query.ts`). */
function watchFetches(client: QueryClient): void {
  if (WATCHED.has(client)) return;
  WATCHED.add(client);
  client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return;
    const { query, action } = event;
    if (action.type !== 'fetch' && action.type !== 'success') return;
    const stamps = STAMPS.get(query) ?? {};
    if (action.type === 'fetch') stamps.inflightAt = Date.now();
    // A manual write (`setQueryData`) is as fresh as it landed; a fetch is as old as it started.
    else
      stamps.dataAt =
        action.manual === true
          ? query.state.dataUpdatedAt
          : (stamps.inflightAt ?? query.state.dataUpdatedAt);
    STAMPS.set(query, stamps);
  });
}

/**
 * When `query`'s data was read, for the `ready` rule; 0 with no data yet. Data
 * that landed before this client's first live read subscribed (none in practice:
 * the shell mounts a live read first) falls back to its `dataUpdatedAt`.
 */
function readStartedAt(
  query: { readonly state: { readonly dataUpdatedAt: number } } | undefined,
): number {
  const landed = query?.state.dataUpdatedAt ?? 0;
  if (query === undefined || landed === 0) return 0;
  return STAMPS.get(query)?.dataAt ?? landed;
}

/**
 * A REST read kept fresh by the account stream. `token` must be the one the
 * shell hub dialled with for the read to go live; any other (a test's injected
 * token, a signed-out caller) reads and polls exactly as `createQuery` would.
 */
export function createLiveQuery<T, K extends QueryKey = QueryKey>(
  options: () => LiveQueryOptions<T, K>,
  token: string | null,
): LiveQuery<T> {
  const client = useQueryClient();
  watchFetches(client);
  const queryOf = (queryKey: QueryKey) => client.getQueryCache().find({ queryKey, exact: true });
  const { status, isLive } = useLiveChanges(
    () => options().live,
    token,
    () => void client.invalidateQueries({ queryKey: untrack(options).queryKey, exact: true }),
    () => readStartedAt(queryOf(untrack(options).queryKey)),
  );
  const query = createQuery<T, Error, T, K>(() => {
    const { live: _held, refreshMs, ...rest } = options();
    const vouched = isLive();
    const read: QueryOptions<T, Error, T, K> & { initialData?: undefined } = {
      ...rest,
      refetchInterval: vouched
        ? (refreshMs ?? false)
        : Math.min(LIVE_FALLBACK_POLL_MS, refreshMs ?? LIVE_FALLBACK_POLL_MS),
      refetchOnWindowFocus: !vouched,
    };
    return read;
  });
  return { query, status, isLive };
}
